import { debounce } from '../../../utils/debounce';
import React, { useMemo } from 'react';
import { Button, UncontrolledTooltip } from 'reactstrap';
import StatusFilter from '../headerFilters/StatusFilter';
import {
    StatusCompleteContainer,
    StatusDangerContainer,
    StatusPendingContainer,
} from '../../common/StatusContainer';
import {
    IoIosCheckmark,
    IoMdChatboxes,
    IoMdClose,
    IoMdPerson,
    IoMdCloudUpload,
} from 'react-icons/io';
import { FaCalendarAlt } from 'react-icons/fa';
import moment from 'moment';
import DateFilter from '../headerFilters/DateFilter';
import AppointmentTimeFilter from '../headerFilters/AppointmentTimeFilter';
import CallingButton from '../../offline-consultations/CallingButton';
import AckCallFilter from '../headerFilters/AckCallFilter';
import VisitTypeFilter from '../headerFilters/VisitTypeFilter';
import StageFilter from '../headerFilters/StageFilter';
import TextInputFilter from '../headerFilters/TextInputFilter';
import ClaimedByFilter from '../headerFilters/ClaimedByFilter';
import TagsFilter from '../headerFilters/TagsFilter';

const commonFields = (accessor, header, numeric = false) => ({
    Header: header,
    accessor: accessor,
    disablePadding: true,
    style: { whiteSpace: 'unset' },
    numeric: numeric,
});

const useTableColumns = (functions, values, deps) => {
    const {
        dispatch,
        showPhysiotherapyRequestModal,
        setRequestIdFilter,
        setParentIdFilter,
        setStatusFilter,
        confirmOpenModal,
        cancelOpenModal,
        setRequestDateFilter,
        setAppTimeFilter,
        setAppointmentDateFilter,
        setAckButtonConfirmModal,
        setAckFilter,
        openClaimRequestButton,
        openEditPaymentButton,
        openRequestCommentModal,
        openShiftRequestButton,
        setRequestCenterFilter,
        setRequestPhysiotherapyFilter,
        setRequestSponsorFilter,
        setVisitTypeFilter,
        setStageFilter,
        openUploadPrescriptionModal,
        openRescheduleModal,
        setTagsFilter,
    } = functions;
    const { statusFilter, visitTypeFilter, stageFilter, ackFilter, tagsFilter } = values;
    const tableColumns = useMemo(
        () => [
            {
                Header: 'Cashless Physiotherapy Request Details',
                foldable: true,
                columns: [
                    {
                        ...commonFields('requestId', 'RID', true),
                        Cell: (row) => {
                            const { requestId } = row.original;
                            return (
                                <div>
                                    <Button
                                        type="submit"
                                        size="sm"
                                        color="primary"
                                        onClick={() =>
                                            dispatch(showPhysiotherapyRequestModal(requestId))
                                        }
                                    >
                                        {requestId}
                                    </Button>
                                </div>
                            );
                        },
                        Filter: () => (
                            <TextInputFilter
                                onChange={(value) => {
                                    debounce(setRequestIdFilter(value), 300);
                                }}
                            // placeholder="RIDd"
                            />
                        ),
                    },
                    {
                        ...commonFields('patientName', 'Patient'),
                        filterable: false,
                    },
                    {
                        ...commonFields('centerName', 'Center'),
                        filterable: true,
                        Filter: () => (
                            <TextInputFilter
                                onChange={(value) => {
                                    debounce(setRequestCenterFilter(value), 300);
                                }}
                            // placeholder="Center"
                            />
                        ),
                    },
                    {
                        Header: 'Visit Type',
                        accessor: 'homeVisit',
                        filterable: true,
                        Filter: () => <VisitTypeFilter onChange={setVisitTypeFilter} value={visitTypeFilter} />,
                        Cell: (row) => {
                            const { homeVisit } = row.original;
                            if (homeVisit === 1) return 'Home Visit';
                            return 'Center Visit';
                        },
                    },
                    {
                        ...commonFields('sponsorName', 'Sponsor'),
                        filterable: true,
                        Filter: () => (
                            <TextInputFilter
                                onChange={(value) => {
                                    debounce(setRequestSponsorFilter(value), 300);
                                }}
                            // placeholder="Sponsor"
                            />
                        ),
                    },
                    {
                        ...commonFields('status', 'Status'),
                        Filter: () => (
                            <StatusFilter onChange={setStatusFilter} value={statusFilter} />
                        ),
                        Cell: (row) => {
                            const { status, cancelledReason, walletRefundAmount, copayRefundAmount, statusMessage } = row.original;
                            const pendingStatuses = [
                                'consultation_requested',
                                'consultation_confirmed',
                                'consultation_reschedule',
                                'pending_session_plan',
                                'sessions_requested',
                                'sessions_approved',
                                'sessions_booked',
                            ];
                            const cancelledStatuses = [
                                'consultation_cancelled',
                                'sessions_cancelled',
                                'sessions_rejected',
                            ];
                            const completedStatuses = [
                                'consultation_completed',
                                'sessions_completed',
                            ];

                            const renderStatus = () => {
                                if (pendingStatuses.includes(status)) {
                                    return (
                                        <StatusPendingContainer>
                                            {status.replace(/_/g, ' ')}
                                        </StatusPendingContainer>
                                    );
                                } else if (cancelledStatuses.includes(status)) {
                                    return (
                                        <>
                                            <StatusDangerContainer>
                                                {status.replace(/_/g, ' ')}
                                            </StatusDangerContainer>
                                            <span>{`(${cancelledReason ? cancelledReason : 'Not Specified'})`}</span>
                                            {walletRefundAmount != null && Number(walletRefundAmount) > 0 && (
                                                <div style={{ marginTop: '4px', fontSize: '11px', color: '#e65100', fontWeight: 600 }}>
                                                    Wallet Refund: ₹{walletRefundAmount}
                                                </div>
                                            )}
                                            {copayRefundAmount != null && Number(copayRefundAmount) > 0 && (
                                                <div style={{ marginTop: '2px', fontSize: '11px', color: '#e65100', fontWeight: 600 }}>
                                                    Copay Refund: ₹{copayRefundAmount}
                                                </div>
                                            )}
                                        </>
                                    );
                                } else if (completedStatuses.includes(status)) {
                                    return (
                                        <StatusCompleteContainer>
                                            {status.replace(/_/g, ' ')}
                                        </StatusCompleteContainer>
                                    );
                                } else {
                                    return (
                                        <StatusPendingContainer>
                                            {status.replace(/_/g, ' ')}
                                        </StatusPendingContainer>
                                    );
                                }
                            };

                            return (
                                <>
                                    {renderStatus()}
                                    {statusMessage && (
                                        <span style={{ fontSize: '11px', color: '#555' }}>
                                            {`(${statusMessage})`}
                                        </span>
                                    )}
                                </>
                            );
                        },
                    },
                    {
                        ...commonFields('stage', 'Stage'),
                        Filter: () => <StageFilter onChange={setStageFilter} value={stageFilter} />,
                        Cell: (row) => {
                            const { status } = row.original;
                            if (
                                [
                                    'consultation_requested',
                                    'consultation_confirmed',
                                    'consultation_completed',
                                    'consultation_cancelled',
                                    'pending_session_plan',
                                    'rescheduled_request',
                                ].includes(status)
                            ) {
                                return 'Consultation';
                            } else if (
                                [
                                    'sessions_requested',
                                    'sessions_approved',
                                    'sessions_booked',
                                    'sessions_completed',
                                    'sessions_cancelled',
                                ].includes(status)
                            ) {
                                return 'Sessions';
                            } else {
                                return 'N/A';
                            }
                        },
                    },
                    {
                        Header: 'Tags',
                        accessor: 'tags',
                        filterable: true,
                        sortable: false,
                        Filter: () => <TagsFilter onChange={setTagsFilter} value={tagsFilter} />,
                        Cell: (row) => {
                            const { partnerTatBreached } = row.original;
                            const tags = [];
                            if (partnerTatBreached) tags.push('Partner TAT Breached');
                            if (tags.length === 0) return '-';
                            return (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    {tags.map((tag) => (
                                        <span key={tag} style={{
                                            padding: '2px 6px',
                                            backgroundColor: '#fff3e0',
                                            border: '1px solid #ff9800',
                                            borderRadius: '4px',
                                            fontSize: '10px',
                                            color: '#e65100',
                                            fontWeight: 600,
                                        }}>
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            );
                        },
                    },
                    {
                        ...commonFields('actions', 'Actions', true),
                        sortable: false,
                        filterable: false,
                        Cell: (row) => {
                            return (
                                <div style={{ alignContent: 'center', gap: '20px' }}>
                                    {(
                                        row.original.status === 'consultation_requested' ||
                                        row.original.status === 'rescheduled_request'
                                    ) ? (
                                        <>
                                            <IoIosCheckmark
                                                className="icon-style"
                                                style={{ fontSize: '25px' }}
                                                id={`confirmButton-${row.original.requestId}`}
                                                onClick={() =>
                                                    confirmOpenModal(
                                                        row.original.requestId,
                                                        row.original.status === 'rescheduled_request'
                                                            ? row.original.rescheduledAppointmentDate
                                                            : row.original.requestedAppointmentDate
                                                    )
                                                }
                                            />
                                            <UncontrolledTooltip
                                                target={`confirmButton-${row.original.requestId}`}
                                            >
                                                Confirm Appointment
                                            </UncontrolledTooltip>
                                        </>
                                    ) : (
                                        <></>
                                    )}
                                    {!['consultation_cancelled', 'sessions_cancelled'].includes(
                                        row.original.status
                                    ) ? (
                                        <>
                                            <IoMdClose
                                                className="icon-style"
                                                id={`cancelButton-${row.original.requestId}`}
                                                onClick={() => cancelOpenModal(row.original.requestId)}
                                            />
                                            <UncontrolledTooltip
                                                target={`cancelButton-${row.original.requestId}`}
                                            >
                                                Cancel Appointment
                                            </UncontrolledTooltip>
                                        </>
                                    ) : (
                                        <></>
                                    )}
                                    {!row.original.opsUserName ? (
                                        <>
                                            <IoMdPerson
                                                className="icon-style"
                                                id={`claimButton-${row.original.requestId}`}
                                                onClick={() =>
                                                    openClaimRequestButton(row.original.requestId)
                                                }
                                            />
                                            <UncontrolledTooltip
                                                target={`claimButton-${row.original.requestId}`}
                                            >
                                                Claim This Request
                                            </UncontrolledTooltip>
                                        </>
                                    ) : (
                                        <></>
                                    )}
                                    {!['consultation_requested', 'sessions_booked', 'sessions_completed', 'consultaion_cancelled'].includes(row.original.status) &&
                                    row.original.appointmentDate &&
                                    moment(row.original.appointmentDate, 'YYYY-MM-DD').isBefore(moment(), 'day') ? (
                                        <>
                                            <IoMdCloudUpload
                                                className="icon-style"
                                                style={{ fontSize: '20px' }}
                                                id={`uploadButton-${row.original.requestId}`}
                                                onClick={() =>
                                                    openUploadPrescriptionModal(row.original.requestId)
                                                }
                                            />
                                            <UncontrolledTooltip
                                                target={`uploadButton-${row.original.requestId}`}
                                            >
                                                Upload Prescription
                                            </UncontrolledTooltip>
                                        </>
                                    ) : (
                                        <></>
                                    )}
                                    {(
                                        row.original.status === 'consultation_requested' ||
                                        row.original.status === 'consultation_confirmed'
                                    ) ? (
                                        <>
                                            <FaCalendarAlt
                                                className="icon-style"
                                                id={`rescheduleButton-${row.original.requestId}`}
                                                onClick={() => openRescheduleModal(row.original.requestId)}
                                            />
                                            <UncontrolledTooltip
                                                target={`rescheduleButton-${row.original.requestId}`}
                                            >
                                                Reschedule
                                            </UncontrolledTooltip>
                                        </>
                                    ) : (
                                        <></>
                                    )}
                                </div>
                            );
                        },
                    },
                    {
                        ...commonFields('requestedCreatedAt', 'Req. Created Date Time'),
                        headerStyle: { overflow: 'visible' },
                        width: 150,
                        filterable: true,
                        Filter: () => (
                            <DateFilter
                                label='Req. Created'
                                uniqueId='reqCreatedAt123'
                                onChooseDate={setRequestDateFilter}
                            />
                        ),
                        Cell: (row) => {
                            const { requestedCreatedAt } = row.original;
                            return requestedCreatedAt || '';
                        },
                    },
                    // {
                    //     ...commonFields('requestedAppointmentDate', 'Req. Date Time'),
                    //     headerStyle: { overflow: 'visible' },
                    //     Cell: (row) => {
                    //         const { requestedAppointmentDate, requestedAppointmentTime } = row.original;
                    //         const dateStr = requestedAppointmentDate
                    //             ? moment(requestedAppointmentDate).format('DD MMM')
                    //             : '';
                    //         const timeStr = requestedAppointmentTime || '';

                    //         if (dateStr && timeStr) {
                    //             return `${dateStr} ${timeStr}`;
                    //         } else if (dateStr) {
                    //             return dateStr;
                    //         } else if (timeStr) {
                    //             return timeStr;
                    //         }
                    //         return '';
                    //     },
                    //     Filter: () => (
                    //         <DateFilter
                    //             label="Request"
                    //             uniqueId="fsfdf343s"
                    //             onChooseDate={setRequestDateFilter}
                    //         />
                    //     ),
                    // },
                    {
                        ...commonFields('appointmentDate', 'Appt. Date'),
                        headerStyle: { overflow: 'visible' },
                        filterable: false,
                        Cell: (row) => {
                            const { appointmentDate } = row.original;
                            return appointmentDate
                                ? moment(appointmentDate, 'YYYY-MM-DD').format('DD MMM')
                                : '';
                        },
                    },
                    {
                        ...commonFields('appointmentTime', 'Appt. Slot'),
                        headerStyle: { overflow: 'visible' },
                        Cell: (row) => {
                            const { appointmentTime } = row.original;
                            try {
                                return appointmentTime;
                            } catch (err) {
                                return appointmentTime || '';
                            }
                        },
                        filterable: false,
                        // Filter: () => (
                        //     <AppointmentTimeFilter
                        //         handleChange={(timingId, slotId) => {
                        //             setAppTimeFilter([timingId, slotId])
                        //         }}
                        //     />
                        // )
                    },
                    {
                        ...commonFields('validTill', 'Session Validity'),
                        headerStyle: { overflow: 'visible' },
                        Cell: (row) => {
                            const { validTill } = row.original;
                            try {
                                return validTill;
                            } catch (err) {
                                return validTill || '';
                            }
                        },
                        filterable: false,
                    },
                    // {
                    //     ...commonFields('slotId', 'Slot Time', true),
                    //     filterable: false,
                    //     Cell: (row) => row.original.slotId || '',
                    // },
                    // {
                    //     ...commonFields('categoryName', 'Category'),
                    //     filterable: false,
                    //     Cell: (row) => {
                    //         const { categoryName } = row.original
                    //         return categoryName || 'N/A'
                    //     }
                    // },
                    // {
                    //     ...commonFields('subCategoryName', 'Physiotherapy Name'),
                    //     filterable:true,
                    //     Filter: () => (
                    //         <input
                    //             onChange={(event) => {
                    //                 debounce(setRequestPhysiotherapyFilter(event.target.value), 300)
                    //             }}
                    //         />
                    //     )
                    // },
                    {
                        ...commonFields('opsUserName', 'Claimed By'),
                        Filter: ({ filter, onChange }) => (
                            <ClaimedByFilter
                                value={filter ? filter.value : ''}
                                onChange={onChange}
                            />
                        ),
                    },
                    {
                        ...commonFields('acknowledgementCall', 'Ack. Call'),
                        Cell: (row) => {
                            const {
                                requestId,
                                userPhone,
                                acknowledgementCallCount,
                                acknowledgementCallDuration,
                            } = row.original;
                            let status = 'Pending';

                            if (
                                acknowledgementCallDuration &&
                                acknowledgementCallDuration !== '0s'
                            ) {
                                status = `Yes (${acknowledgementCallDuration}, ${acknowledgementCallCount})`;
                                return (
                                    <StatusCompleteContainer>
                                        {status}
                                        {/* {userPhone && (
                                        <CallingButton
                                            onSubmit={() => setAckButtonConfirmModal(requestId)}
                                        />
                                    )} */}

                                        <CallingButton
                                            onSubmit={() => setAckButtonConfirmModal(requestId)}
                                        />
                                    </StatusCompleteContainer>
                                );
                            } else if (
                                acknowledgementCallCount &&
                                parseInt(acknowledgementCallCount) !== 0
                            ) {
                                status = `No Answer (${acknowledgementCallCount})`;
                                return (
                                    <StatusPendingContainer>
                                        {status}
                                        {/* {userPhone && (
                                    <CallingButton
                                        onSubmit={() => setAckButtonConfirmModal(requestId)}
                                    />
                                )} */}
                                        <CallingButton
                                            onSubmit={() => setAckButtonConfirmModal(requestId)}
                                        />
                                    </StatusPendingContainer>
                                );
                            }
                            return (
                                <StatusDangerContainer>
                                    {status}
                                    {/* {userPhone && (
                                    <CallingButton
                                        onSubmit={() => setAckButtonConfirmModal(requestId)}
                                    />
                                )} */}
                                    <CallingButton
                                        onSubmit={() => setAckButtonConfirmModal(requestId)}
                                    />
                                </StatusDangerContainer>
                            );
                        },
                        Filter: () => <AckCallFilter onChange={setAckFilter} value={ackFilter} />,
                    },
                    {
                        ...commonFields('commentsCount', 'Comments'),
                        filterable: false,
                        sortable: false,
                        Cell: (row) => {
                            const { requestId, commentsCount } = row.original;
                            return (
                                <React.Fragment>
                                    <div onClick={() => openRequestCommentModal(requestId)}>
                                        <p>
                                            <span id={`commentTooltip-a-${requestId}`}>
                                                {commentsCount}
                                                <IoMdChatboxes
                                                    className="icon-chat"
                                                    style={{
                                                        cursor: 'pointer',
                                                        width: '30px',
                                                        height: '30px',
                                                    }}
                                                />
                                            </span>
                                        </p>
                                        <UncontrolledTooltip
                                            target={`commentTooltip-a-${requestId}`}
                                        >
                                            View Comments
                                        </UncontrolledTooltip>
                                    </div>
                                </React.Fragment>
                            );
                        },
                    },
                ],
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
        ],
        deps
    );

    return tableColumns;
};

export default useTableColumns;
